import { Complete } from "@lyfegen-platform/react-common";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SelectableValue } from "../../models/common/selectable-value";
import { distinctBy } from "../../utils/distinct-by";
import modelLibraryStyles from "../model-library/model-library-styles";
import NewCompleteInput from "../NewCompleteInput";

const Paper: React.FC = ({ children }) => {
	return <>{children}</>;
};

interface ScopeOptionsProps {
	id: string;
	options: Array<SelectableValue>;
	index: number;
	values?: Array<SelectableValue | undefined>;
	handleFilter: React.Dispatch<React.SetStateAction<SelectableValue[]>>;
	clearAll: boolean;
	label: string;
	fullWidth?: boolean;
	disabled?: boolean;
	optionLabel?: string;
	property: string;
}

const DropDownMultipleSelection: React.FC<ScopeOptionsProps> = ({
	options,
	property,
	values,
	handleFilter,
	clearAll,
	label,
	id,
	fullWidth,
	disabled,
	optionLabel,
}) => {
	const [selected, setSelected] = useState<Array<SelectableValue | undefined>>(values ?? []);
	const valuesRef = useRef(values);

	const { t } = useTranslation();
	const { input, inputFocus, optionsRoot, endAdornment } = modelLibraryStyles();

	useEffect(() => {
		if (clearAll === true) {
			setSelected([]);
		}
	}, [clearAll]);

	useEffect(() => {
		const sortedSelectedValues = selected.sort();

		if (
			!!values &&
			!(
				valuesRef.current &&
				valuesRef.current.length === values.length &&
				values
					.sort()
					.every(
						(value, index) =>
							value?.value === sortedSelectedValues[index]?.value && value?.label === sortedSelectedValues[index]?.label
					)
			)
		) {
			setSelected(values);
			valuesRef.current = values;
		}
	}, [values]);

	const uniqueOptions = [...distinctBy(options ?? [], (o) => o?.value)];
	const handleSelect = (val: Array<SelectableValue>) => {
		const uniqueVal = [...distinctBy(val ?? [], (o) => o?.value)];
		const opt = uniqueOptions.filter((uni) => uniqueVal.some((s) => s.value === uni.value));
		setSelected(opt);
		handleFilter(val);
	};

	const handleChange = (
		event: React.ChangeEvent<Record<any, unknown>>,
		v: any[],
		reason: "select-option" | "remove-option" | "clear" | "create-option" | "blur"
	) => {
		event.preventDefault();
		switch (reason) {
			case "select-option":
			case "remove-option":
				handleSelect(v);
				break;
			case "clear":
				handleSelect([]);
				break;
			default:
				break;
		}
	};

	if (!options) return null;

	return (
		<Complete
			id={id}
			value={selected as any}
			options={[...selected, ...uniqueOptions.filter((o) => !selected.includes(o || undefined))]}
			multiple
			onChange={handleChange}
			classes={{ inputRoot: fullWidth ? undefined : input, focused: inputFocus, popper: optionsRoot, endAdornment: endAdornment }}
			style={{ width: fullWidth ? "100%" : undefined }}
			PaperComponent={Paper}
			getOptionSelected={(a: any, b: any) => a === b || (!!property && a[property] === b[property])}
			disabled={disabled}
			disableClearable
			disableCloseOnSelect
			filterSelectedOptions
			optionLabel={"label"}
			noOptionsText={t("no_options_available")}
			getOptionLabel={(v) => t(v[optionLabel ?? property])}
			clearOnBlur={false}
			size="small"
			renderInput={(params) => {
				return (
					<NewCompleteInput
						label={label}
						params={{
							...params,
							InputProps: {
								...params.InputProps,
								startAdornment: (params.InputProps.startAdornment as Array<any> | undefined)?.map((sa, i) => ({
									...sa,
									props: { ...sa.props, label: t((selected[i]! as any)[optionLabel ?? property]) },
								})),
							},
							inputProps: {
								...params.inputProps,
							},
						}}
					/>
				);
			}}
		/>
	);
};

export default DropDownMultipleSelection;
